/**
 * @graphql cc
 */
import React, { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { fg } from '@confluence/feature-gating';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';

import { ReadTimePopupWrapper } from './ReadTimePopupWrapper.tsx';
import {
	getContentFeatureExtractedEventObject,
	getContentFeatureExtractedEventObjectFromFeatures,
} from './readTimeUtils.ts';
import { useReadTime } from './hooks/useReadTime.tsx';

export const OriginalReadTime = ({
	contentId,
	isAbbreviatedReadTime,
	isDetailsPanel,
	showWordCount,
}: {
	contentId: string;
	isAbbreviatedReadTime?: boolean;
	isDetailsPanel?: boolean;
	showWordCount?: boolean;
}): React.JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		adf,
		loading,
		error,
		contentType,
		readTime,
		wordCount,
		features,
		featuresSkipExpand,
		isReadTimeCapped,
	} = useReadTime(contentId);

	useEffect(() => {
		if (createAnalyticsEvent && adf && !loading && !error && contentId && contentType) {
			if (fg('read_time_parse_dedup')) {
				if (features) {
					createAnalyticsEvent(
						getContentFeatureExtractedEventObjectFromFeatures({
							contentType,
							contentId,
							features,
							isInEditor: false,
						}),
					).fire();
				}
			} else {
				createAnalyticsEvent(
					getContentFeatureExtractedEventObject({ contentType, contentId, adf, isInEditor: false }),
				).fire();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- features derived from adf
	}, [contentId, loading, error, contentType, createAnalyticsEvent, adf, features]);

	if (error) {
		return <ErrorDisplay error={error} />;
	}
	if (readTime || (showWordCount && wordCount !== null)) {
		return (
			<ReadTimePopupWrapper
				contentId={contentId}
				isAbbreviatedReadTime={isAbbreviatedReadTime}
				isDetailsPanel={isDetailsPanel}
				showWordCount={showWordCount}
				features={features}
				featuresSkipExpand={featuresSkipExpand}
				wordCount={wordCount}
				readTime={readTime}
				source="originalReadTime"
				isReadTimeCapped={isReadTimeCapped}
			/>
		);
	}

	return null;
};
